<template>
  <div v-loading="loading" >
    <div v-if="!isValid" >
      Merci de sélectionner une dimension compatible (code ZE, code département, code région).
    </div>
    <template v-else >
      <vue-map
         v-if="geojson"
         :zoomable="false"
         :geojson="geojson"
         :featureColor="color"
         :featureContent="popoverComponent"
         :featureData="popoverData"
         :minRadius="radius.min"
         :maxRadius="radius.max"
         :color="d => secondaryColor"
         :data="data2"
         :width="width"
         :height="height" />
      <div class="menu" >
        <transition
           enter-active-class="animated slideInLeft"
           leave-active-class="animated slideOutLeft" >
          <div v-if="!minimized" class="container" >
            <i class="reduce el-icon-fa-minus-square-o" @click="minimized = true" />
            <geo-selector v-if="geoAPI" v-model="location" :geo-api="geoAPI" @input="loadFeatures" />
            <div>
              <div>Afficher les zones limitrophes</div>
              <el-switch v-model="neighbors" @change="loadFeatures" />
            </div>
            <div class="legend" >
              <div>{{ yFormatter(domain[0]) }}</div>
              <div class="scale" >
                <span v-for="i in 100" :key="i" :style="scaleTick(i)" />
              </div>
              <div>{{ yFormatter(domain[1]) }}</div>
            </div>
          </div>
        </transition>
        <transition :duration="1000" enter-active-class="hidden" leave-active-class="hidden" >
          <i v-if="minimized" class="restore el-icon-fa-bars" @click="minimized = false" />
        </transition>
      </div>
    </template>
  </div>
</template>

<script>
import VueMap from 'd3vue/components/Map.vue'
import MapFeaturePopover from './MapFeaturePopover'
import GeoSelector from './GeoSelector.vue'
import * as d3Chromatic from 'd3-scale-chromatic'
import * as d3 from 'd3'
import featureCache from './FeatureCache'

export default {
  components: {
    VueMap,
    GeoSelector
  },
  props: {
    values: {
      required: true
    },
    dimension: {
      type: Object
    },
    data: {
      required: true
    },
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    secondaryValue: {
      type: Boolean,
      required: true
    },
    yFormatter: {
      type: Function,
      default: v => v
    },
    y2Formatter: {
      type: Function,
      default: v => v
    },
    secondaryColor: {
      type: String,
      default: '#000'
    },
    colorScale: {
      default: () => {
        return d3.scaleSequential(d3Chromatic.interpolateBlues)
      }
    }
  },
  data () {
    return {
      neighbors: true,
      loading: false,
      geojson: null,
      location: null,
      minimized: false,
      geoAPI: featureCache.api,
      acceptableDimensions: {
        '[Zone emploi].[Code zone emploi]': 'App\\Entity\\Geo\\EmploymentZone',
        '[Commune].[Code région]': 'App\\Entity\\Geo\\Region',
        '[Commune].[Code département]': 'App\\Entity\\Geo\\Department',
        '[Département].[Code département]': 'App\\Entity\\Geo\\Department',
        '[Département].[Code région]': 'App\\Entity\\Geo\\Region'
      }
    }
  },
  computed: {
    radius () {
      const base = Math.min(this.width, this.height)
      return {
        min: Math.max(2, base * 0.002),
        max: Math.min(20, base * 0.02)
      }
    },
    popoverComponent () {
      return () => MapFeaturePopover
    },
    isValid () {
      if (!this.dimension) {
        return false
      }
      return typeof this.acceptableDimensions[this.dimension.value.UniqueName] !== 'undefined'
    },
    data2 () {
      if (!this.secondaryValue || !this.geojson || !this.geojson.features) {
        return []
      }
      return this.geojson.features.map(feature => {
        if (typeof this.dataAsDict[feature.properties.code] === 'undefined') {
          return null
        }
        if (typeof this.dataAsDict[feature.properties.code].value2 === 'undefined') {
          return null
        }
        return {
          lat: feature.properties.lat,
          lng: feature.properties.lng,
          value: this.dataAsDict[feature.properties.code].value2
        }
      }).filter(d => d)
    },
    scale () {
      return this.colorScale.domain(this.domain)
    },
    domain () {
      let min = null
      let max = null
      this.data.forEach(d => {
        if (min === null || min > d.value) {
          min = d.value
        }
        if (max === null || max < d.value) {
          max = d.value
        }
      })
      return [min, max]
    },
    dataAsDict () {
      const dict = {}
      this.data.forEach(d => {
        dict[d.label] = d
      })
      return dict
    },
    popoverData () {
      return feature => {
        if (typeof this.dataAsDict[feature.properties.code] === 'undefined') {
          return {
            properties: feature.properties,
            values: this.values,
            formatter: this.yFormatter
          }
        }
        return {
          properties: feature.properties,
          value: this.dataAsDict[feature.properties.code].value,
          value2: this.dataAsDict[feature.properties.code].value2,
          values: this.values,
          formatter: this.yFormatter,
          formatter2: this.y2Formatter
        }
      }
    },
    color () {
      return feature => {
        if (typeof this.dataAsDict[feature.properties.code] === 'undefined') {
          return '#FFF'
        }
        return this.scale(this.dataAsDict[feature.properties.code].value)
      }
    }
  },
  mounted () {
    this.loadFeatures()
  },
  watch: {
    dimension () {
      if (this.isValid) {
        this.loadFeatures()
      }
    }
  },
  methods: {
    scaleTick (i) {
      return {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '1px',
        left: `${i - 1}px`,
        'background-color': this.colorScale.copy().domain([0, 100])(i - 1)
      }
    },
    loadFeatures () {
      this.loading = true
      if (this.location) {
        if (this.neighbors) {
          featureCache.features(
            this.acceptableDimensions[this.dimension.value.UniqueName],
            this.location.id,
            true
          ).then((data) => {
            this.geojson = data
            this.loading = false
          })
        } else {
          featureCache.features(
            this.acceptableDimensions[this.dimension.value.UniqueName],
            this.location.id
          ).then((data) => {
            this.geojson = data
            this.loading = false
          })
        }
      } else {
        featureCache
          .features(this.acceptableDimensions[this.dimension.value.UniqueName])
          .then((data) => {
            this.geojson = data
            this.loading = false
          })
      }
    }
  }
}
</script>

<style scoped>
.menu {
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    max-width: 280px;
    min-width: 50px;
    overflow: hidden;
}
.restore {
    position: absolute;
    top: 0px;
    left: 0px;
    color: #d6d6d7;
    cursor: pointer;
}
.hidden {
    display: none;
}
.reduce {
    position: absolute;
    top: 5px;
    right: 5px;
    color: #d6d6d7;
    cursor: pointer;
}
.menu .container {
    background-color: #FFF;
    border: 1px solid #d6d6d7;
    padding: 10px;
}
.legend {
    display: flex;
    margin-top: 30px;
    justify-content: center;
}
.legend .scale {
    width: 100px;
    position: relative;
    height: 1em;
    display: inline-block;
}
.legend .scale img {
    width: 100%;
    height: 100%;
}
* >>> .axes line {
    stroke: #000;
}
</style>



// WEBPACK FOOTER //
// src/graphs/Map.vue